import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

/**
 * Makes one set of responsible persons across the Space records selected in
 * the records list.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.select.single.Component,
    ds.layout.widget.Component,
    ds.notice.Component,
    ds.overlay.centered.Component,
    shared.employeeSelector.Component,
  ],
  selector: 'atn-space-bulk-responsible-persons-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.full.records.plugins.bulkResponsiblePersons.Plugin} */
  public readonly bulkResponsiblePersonsPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.bulkResponsiblePersons.Plugin)
    .get()

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** Employees being made responsible */
  public readonly selectedEmployeesSg = ngCore.computed(
    (
      employeeIds = this.bulkResponsiblePersonsPlugin.selectors.employeeIds.asSignal(),
      employeeLookup = this.employeesEc.plugins.cachePlugin.feature.selectors.lookup.asSignal(),
    ) =>
      employeeIds
        .map(employeeId => employeeLookup[employeeId])
        .filter(TypeChecker.isNotNullish.raw),
  )

  /**
   * Same as {@link selectedEmployeesSg} but in the form of {@link ds.input.select.models.Item}.
   */
  public readonly employeeItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.Employee.Id>[]
  >((selectedEmployees = this.selectedEmployeesSg()) =>
    selectedEmployees.map(employee => ({
      label: employee.name,
      value: employee.id,
    })),
  )

  /** Modal title, naming how many records the responsible persons apply to */
  public readonly titleSg = ngCore.computed(
    (
      recordCount = this.recordsEc.plugins.selectionPlugin.feature.selectors
        .getAllSelectedIds({
          target: state.space.full.records.SPACE_RECORDS_SELECTION_TARGET,
        })
        .asSignal().length,
    ) =>
      `Update Responsible Person(s) (${recordCount} Space ${
        recordCount === 1 ? 'Record' : 'Records'
      } selected)`,
  )

  /** Adds or removes one of the employees being made responsible */
  public employeeToggledHandler(
    employeeId: ents.extra.space.Employee.Id,
    selected: boolean,
  ): void {
    this.bulkResponsiblePersonsPlugin.actions.toggleEmployees
      .from('Space.BulkResponsiblePersons.Modal.EmployeeCheckbox.Toggled')
      .dispatch({ employeeIds: [employeeId], selected })
  }

  /** Removes an employee from the selection (tag close button) */
  public removeSelectedEmployee(
    employeeId: ents.extra.space.Employee.Id,
  ): void {
    this.bulkResponsiblePersonsPlugin.actions.toggleEmployees
      .from('Space.BulkResponsiblePersons.Modal.SelectedTag.Removed')
      .dispatch({ employeeIds: [employeeId], selected: false })
  }

  /** Stores the employee the user picked as the primary */
  public primaryEmployeeChangedHandler(
    primaryEmployeeId: ents.extra.space.Employee.Id | undefined,
  ): void {
    this.bulkResponsiblePersonsPlugin.actions.setPrimaryEmployee
      .from('Space.BulkResponsiblePersons.Modal.PrimaryEmployee.Changed')
      .dispatch(primaryEmployeeId)
  }

  /** Applies the responsible persons to every selected record */
  public saveClickHandler(): void {
    this.bulkResponsiblePersonsPlugin.actions.submit
      .from('Space.BulkResponsiblePersons.Modal.SaveButton.Clicked')
      .dispatch()
  }

  /** Closes the modal without applying anything */
  public cancelClickHandler(): void {
    this.bulkResponsiblePersonsPlugin.actions.cancel
      .from('Space.BulkResponsiblePersons.Modal.CancelButton.Clicked')
      .dispatch()
  }
}
